import { useMemo, useState } from "react";
import { MapPin, Phone, Clock, ArrowUpRight, Search } from "lucide-react";
import { LOCATIONS } from "../data/content";

export default function Locations() {
  const [active, setActive] = useState("All");
  const [q, setQ] = useState("");
  const cities = ["All", ...new Set(LOCATIONS.map((l) => l.city))];

  const filtered = useMemo(() => {
    return LOCATIONS.filter((l) => {
      const matchCity = active === "All" || l.city === active;
      const matchQ =
        q.trim() === "" ||
        l.name.toLowerCase().includes(q.toLowerCase()) ||
        l.addr.toLowerCase().includes(q.toLowerCase());
      return matchCity && matchQ;
    });
  }, [active, q]);

  return (
    <div data-testid="locations-page" className="pt-28">
      <section className="py-20 sm:py-24">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 text-center">
          <span className="text-xs tracking-[0.32em] uppercase text-[#C76D4B]">Locations</span>
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[1]">
            Find a Stories <em className="italic text-[#C76D4B]">near you.</em>
          </h1>
          <p className="mt-8 text-[#7A5C45] max-w-xl mx-auto">
            Six cafés, one shared promise. Conversation-first cafés across India's most-loved neighbourhoods.
          </p>
        </div>
      </section>

      <section className="px-5 sm:px-8">
        <div className="max-w-7xl mx-auto bg-[#F3EBE1] rounded-3xl p-5 sm:p-6 flex flex-col gap-4">
          <div className="relative">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#7A5C45]" />
            <input
              data-testid="location-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by café, neighbourhood or city"
              className="w-full bg-white rounded-full pl-11 pr-4 py-3 text-sm border border-[#E8D8C8] focus:outline-none focus:border-[#C76D4B]"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            {cities.map((c) => (
              <button
                key={c}
                data-testid={`city-${c.toLowerCase()}`}
                onClick={() => setActive(c)}
                className={`whitespace-nowrap rounded-full px-5 py-2 text-sm transition border ${
                  active === c
                    ? "bg-[#2A1E17] text-[#FAF7F2] border-[#2A1E17]"
                    : "bg-white text-[#4A3320] border-[#E8D8C8] hover:border-[#C76D4B]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 mb-12 sm:mb-16">
          <div className="relative rounded-[2rem] overflow-hidden border border-[#E8D8C8] h-[420px]">
            <iframe
              title="All Stories cafés"
              data-testid="locations-main-map"
              src="https://maps.google.com/maps?q=India&t=&z=5&ie=UTF8&iwloc=&output=embed"
              className="absolute inset-0 w-full h-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
            <div className="absolute top-5 left-5 bg-[#FAF7F2] rounded-full px-4 py-2 text-[10px] tracking-[0.22em] uppercase text-[#C76D4B] border border-[#E8D8C8]">
              Stories Across India
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          {filtered.length === 0 ? (
            <div className="text-center py-20 text-[#7A5C45]" data-testid="locations-empty">
              No cafés match. Try another city.
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-7">
              {filtered.map((l, i) => (
                <article
                  key={l.name}
                  data-testid={`location-${i}`}
                  className="card-lift bg-white rounded-3xl overflow-hidden border border-[#E8D8C8] grid grid-cols-1 sm:grid-cols-5"
                >
                  <div className="sm:col-span-2 overflow-hidden">
                    <img src={l.img} alt={l.name} className="w-full h-full min-h-[220px] object-cover img-zoom" />
                  </div>
                  <div className="sm:col-span-3 p-6 sm:p-7 flex flex-col">
                    <div className="text-[11px] tracking-[0.22em] uppercase text-[#C76D4B]">{l.city}</div>
                    <h3 className="font-serif-display text-2xl mt-2 leading-tight">{l.name}</h3>
                    <ul className="space-y-2.5 text-sm text-[#4A3320] mt-4">
                      <li className="flex gap-2.5"><MapPin size={15} className="text-[#7A5C45] mt-0.5 flex-none" /> {l.addr}</li>
                      <li className="flex gap-2.5"><Clock size={15} className="text-[#7A5C45] mt-0.5 flex-none" /> {l.hours}</li>
                      <li className="flex gap-2.5"><Phone size={15} className="text-[#7A5C45] mt-0.5 flex-none" /> {l.phone}</li>
                    </ul>
                    <div className="flex flex-wrap gap-2 mt-5">
                      {l.tags.map((t) => (
                        <span key={t} className="text-[10px] tracking-[0.18em] uppercase border border-[#E8D8C8] text-[#7A5C45] rounded-full px-3 py-1">
                          {t}
                        </span>
                      ))}
                    </div>
                    <div className="mt-6 flex gap-3">
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(l.addr)}`}
                        target="_blank"
                        rel="noreferrer"
                        className="btn-primary text-sm py-3"
                        data-testid={`location-directions-${i}`}
                      >
                        Get Directions <ArrowUpRight size={16} />
                      </a>
                      <a href="/contact" className="btn-secondary text-sm py-3" data-testid={`location-reserve-${i}`}>
                        Reserve a Corner
                      </a>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
